// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// PhotoLog — Hands-Free photo/vision entry (T-202).
//
// Snap or upload a photo (whiteboard, meter, harvest tag, handwritten notes);
// GPT-4o vision extracts the workflow fields via hands-free-vision, then the
// same resolve pipeline runs and the review item is handed to the parent — so
// the ReviewCard handles review + commit exactly as with voice/text.

import { useCallback, useRef, useState } from "react";
import { AlertTriangle, Camera, Loader2, RotateCcw, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useFacility } from "@/contexts/FacilityContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { HandsFreeContext, PipelineResult, WorkflowType } from "./types";
import { hfCard, hfCardHeader, hfCardTitle, hfEmptyState, hfWarnAlert, hfWarnBody, hfWarnTitle } from "./handsFreeUi";
import { cn } from "@/lib/utils";
import { WORKFLOW_LABELS, WORKFLOW_TYPES } from "./types";
import { callVisionLog } from "./transcribe";

const MAX_BYTES = 7 * 1024 * 1024; // 7 MB raw

interface PhotoLogProps {
  onPipelineResult: (result: PipelineResult) => void;
  onReset?: () => void;
  /** Pin the workflow and hide the selector (embedded mode). */
  fixedWorkflow?: WorkflowType;
  context?: HandsFreeContext;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(file);
  });
}

export function PhotoLog({ onPipelineResult, onReset, fixedWorkflow, context }: PhotoLogProps) {
  const { activeFacility } = useFacility();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>("image/jpeg");
  const [workflow, setWorkflow] = useState<WorkflowType | "auto">(fixedWorkflow ?? "auto");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState<PipelineResult | null>(null);

  const handleFile = useCallback(async (file: File | undefined) => {
    setError(null);
    setPartial(null);
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Image is too large (max 7 MB). Try a smaller photo.");
      return;
    }
    try {
      const url = await readFileAsDataUrl(file);
      setDataUrl(url);
      setMimeType(file.type);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read the image.");
    }
  }, []);

  const submit = useCallback(async () => {
    if (!activeFacility?.id || !dataUrl || submitting) return;
    setSubmitting(true);
    setError(null);
    setPartial(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setError("Session expired. Please refresh the page and try again.");
        return;
      }
      const supabaseUrl =
        ((import.meta as Record<string, unknown>).env?.VITE_SUPABASE_URL as string | undefined) ||
        "https://bjvlynqjoetvrbbfvhod.supabase.co";

      const res = await callVisionLog({
        imageBase64: dataUrl,
        mimeType,
        facilityId: activeFacility.id,
        accessToken,
        supabaseUrl,
        workflowHint: workflow,
        context: context
          ? { ...context, ...(fixedWorkflow ? { workflow: fixedWorkflow } : {}) }
          : fixedWorkflow
            ? { workflow: fixedWorkflow }
            : undefined,
      });

      if (!res.ok) {
        setError(res.error);
        return;
      }
      const result: PipelineResult = {
        ...res.result,
        workflow_type: res.result.workflow_type ?? (workflow === "auto" ? undefined : workflow),
      };
      if (result.status === "parse_failed" || result.status === "resolve_failed") {
        setPartial(result);
        return;
      }
      onPipelineResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [activeFacility?.id, dataUrl, mimeType, submitting, workflow, onPipelineResult]);

  const reset = useCallback(() => {
    setDataUrl(null);
    setError(null);
    setPartial(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onReset?.();
  }, [onReset]);

  if (!activeFacility) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>No facility selected</AlertTitle>
            <AlertDescription>
              Select an active facility before logging. The entry is scoped to your
              current facility.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={hfCard}>
      <CardHeader className={hfCardHeader}>
        <CardTitle className={cn(hfCardTitle, "flex items-center gap-2")}>
          <Camera className="h-4 w-4 text-primary" />
          Photo
        </CardTitle>
        <CardDescription>
          Snap a whiteboard, meter reading, or harvest tag — we'll read the values for you.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Workflow selector (hidden when pinned via fixedWorkflow) */}
        {!fixedWorkflow && (
          <div className="flex flex-wrap gap-1.5">
            {(["auto", ...WORKFLOW_TYPES] as const).map((w) => (
              <Button
                key={w}
                type="button"
                size="sm"
                variant={workflow === w ? "default" : "outline"}
                onClick={() => setWorkflow(w)}
                disabled={submitting}
                className="text-xs"
              >
                {w === "auto" ? "Auto-detect" : WORKFLOW_LABELS[w]}
              </Button>
            ))}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        {dataUrl ? (
          <div className="space-y-2">
            <img
              src={dataUrl}
              alt="Selected"
              className="max-h-64 w-full rounded-md border object-contain bg-muted"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
            >
              <Camera className="mr-1.5 h-3.5 w-3.5" />
              Choose a different photo
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
            className={cn(hfEmptyState, "w-full py-14 hover:bg-muted/20 transition-colors")}
          >
            <Camera className="h-8 w-8 text-muted-foreground" />
            <span className="mt-2 font-medium text-foreground">Take or choose a photo</span>
            <span className="text-xs text-muted-foreground">Whiteboards, meters, tags — anything with readable values</span>
          </button>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Couldn't process that</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {partial?.status === "parse_failed" && (
          <Alert className={hfWarnAlert}>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className={hfWarnTitle}>Couldn't read the image</AlertTitle>
            <AlertDescription className={hfWarnBody}>
              We couldn't extract structured data from this photo. Try a clearer,
              better-lit shot of the values.
            </AlertDescription>
          </Alert>
        )}

        {partial?.status === "resolve_failed" && (
          <Alert className={hfWarnAlert}>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className={hfWarnTitle}>Couldn't match entities</AlertTitle>
            <AlertDescription className={hfWarnBody}>
              The values were read but the location couldn't be matched to this
              facility. Open pending review to finish manually.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          {(dataUrl || error || partial) && (
            <Button variant="outline" size="sm" onClick={reset} disabled={submitting}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          )}
          <Button onClick={submit} disabled={submitting || !dataUrl} className="min-w-[120px]">
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Reading…
              </>
            ) : (
              <>
                <Sparkles className="mr-1.5 h-4 w-4" />
                Extract &amp; review
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default PhotoLog;
